import React from "react";
import { TextField, Typography, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { ValidationResult } from "ndr-designsystem";

const StyledInput = styled(TextField)<{ error?: boolean }>(({ theme, error }) => ({
    "& .MuiInput-underline:before": { display: "none" },
    "& .MuiInput-underline:after": { display: "none" },
    "& .MuiFormLabel-asterisk": { color: theme.palette.error.main },
    "& .MuiInputBase-input": {
        fontWeight: 500,
        fontSize: "1rem",
        color: theme.palette.text.primary,
        paddingTop: 4,
        paddingBottom: 4,
        borderBottom: `2px solid ${error ? theme.palette.error.main : theme.palette.grey[300]}`,
        transition: "border-color 200ms ease",
        background: "transparent",
        "&:focus": {
            borderBottomColor: error ? theme.palette.error.main : theme.palette.primary.main,
        },
    },
}));

interface TextInputProps {
    label: string;
    value?: string | number | null;
    isEditing?: boolean;
    onChange?: (value: string) => void;
    /** Validates the current value; the error message is shown below the field. */
    checkValue?: (value: string) => ValidationResult;
    maxLength?: number;
    required?: boolean;
}

const TextInput: React.FC<TextInputProps> = ({ label, value, isEditing, onChange, checkValue, maxLength, required }) => {
    const { t } = useTranslation("validation");

    if (isEditing && onChange) {
        const raw = `${value ?? ""}`;
        // Empty fields stay neutral: the asterisk marks them as required.
        // Red + message only appear for actually invalid input (wrong format, too long, ...).
        const validation = raw !== "" && checkValue ? checkValue(raw) : { valid: true, error: null };
        return (
            <Box>
                <StyledInput
                    label={label}
                    value={value ?? ""}
                    required={required}
                    onChange={(e) => onChange(e.target.value)}
                    variant="standard"
                    fullWidth
                    size="small"
                    error={!validation.valid}
                    inputProps={{ maxLength }}
                    InputLabelProps={{ shrink: true, sx: { fontWeight: 600, letterSpacing: "0.04em" } }}
                />
                {!validation.valid && validation.error && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                        {t(validation.error)}
                    </Typography>
                )}
            </Box>
        );
    }

    const renderedLabel = required ? (
        <>{label}<Box component="span" sx={{ color: "error.main", fontWeight: 700 }}> *</Box></>
    ) : label;

    return (
        <TextField
            label={renderedLabel}
            value={value === undefined || value === null || value === "" ? "—" : value}
            variant="standard"
            fullWidth
            size="small"
            InputProps={{ readOnly: true, disableUnderline: true }}
            InputLabelProps={{ sx: { fontWeight: 600, letterSpacing: "0.04em" } }}
            sx={{ "& .MuiInputBase-input": { fontWeight: 500, py: 0.5 } }}
        />
    );
};

TextInput.defaultProps = {
    value: undefined,
    isEditing: false,
    onChange: undefined,
    checkValue: undefined,
    maxLength: undefined,
    required: false,
};

export default TextInput;
